import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { ethers } from "ethers";
import { useWeb3 } from "../../context/Web3Context";
import { useContract } from "../../hooks/useContract";
import SubTabs from "../../components/SubTabs/SubTabs";
import Loader from "../../components/Loader/Loader";
import GlobalPackages from "./GlobalPackages";
import GlobalIncome from "./GlobalIncome";
import GlobalTeam from "./GlobalTeam";
import { PLAN2_PKG_LABELS } from "../../utils/constants";
import { NETWORK, PLAN2_PKG_COUNT, PLAN2_MATRIX_LEVELS } from "../../config/config";
import { toast } from "react-toastify";
import "../../pages/StarTab/StarTab.css";

const SUB_TABS = [
  { key: "packages", label: "Packages" },
  { key: "income", label: "Income" },
  { key: "team", label: "Team" },
];

export default function GlobalTab() {
  const { account } = useWeb3();
  const contracts = useContract();
  const [activeTab, setActiveTab] = useState("packages");
  const [userId, setUserId] = useState(null);
  const [prices, setPrices] = useState([]);
  const [packages, setPackages] = useState(null);
  const [pending, setPending] = useState([]);
  const [incomeStats, setIncomeStats] = useState(null);
  const [pendingRoyalty, setPendingRoyalty] = useState(null);
  const [loadingPkg, setLoadingPkg] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    if (account && contracts) loadData();
    // eslint-disable-next-line
  }, [account, contracts]);

  async function loadData() {
    setPageLoading(true);
    try {
      const uid = await contracts.data.addrToId(account);
      const uidNum = uid.toNumber();
      setUserId(uidNum);
      if (uidNum === 0) { setPageLoading(false); return; }

      const [priceArr, allPkgs, pendingArr, stats, royalty] = await Promise.all([
        contracts.plan2.getPackagePrices(),
        contracts.plan2.getAllPackages(uidNum),
        contracts.plan2.getAllPendingGlobal(uidNum),
        contracts.plan2.getIncomeStats(uidNum),
        contracts.plan2.getPendingRoyalty(uidNum),
      ]);
      setPrices(priceArr);
      setPackages(allPkgs);
      setPending(pendingArr);
      setIncomeStats({ direct: stats.direct, level: stats.lvl, global: stats.glob, royalty: stats.royalty });
      setPendingRoyalty(royalty);
    } catch (err) {
      console.error("GlobalTab load:", err);
      toast.error("Failed to load Global plan data");
    } finally {
      setPageLoading(false);
    }
  }

  async function handleActivate(pkg) {
    if (!contracts || !userId) return;
    setLoadingPkg(pkg);
    try {
      const price = prices[pkg];
      const allowance = await contracts.token.allowance(account, NETWORK.contracts.plan2);
      if (allowance.lt(price)) {
        toast.info("Approving USDT...");
        const tx = await contracts.token.approve(NETWORK.contracts.plan2, ethers.constants.MaxUint256);
        await tx.wait();
        toast.success("USDT approved!");
      }
      // Simulate to get real revert reason
      try { await contracts.plan2.callStatic.activatePackage(pkg); }
      catch (sim) { throw new Error(sim.reason || sim.errorName || sim.message || "Simulation failed"); }
      toast.info("Activating package...");
      const tx = await contracts.plan2.activatePackage(pkg);
      await tx.wait();
      toast.success(`${PLAN2_PKG_LABELS[pkg]} activated!`);
      await loadData();
    } catch (err) { toast.error(err.reason || err.message || "Activation failed"); }
    finally { setLoadingPkg(null); }
  }

  async function handleClaimAllGlobal() {
    if (!contracts) return;
    setLoadingPkg(-1);
    try {
      const tx = await contracts.plan2.claimAllGlobalShares();
      await tx.wait();
      toast.success("All global shares claimed!");
      await loadData();
    } catch (err) { toast.error(err.reason || err.message || "Claim failed"); }
    finally { setLoadingPkg(null); }
  }

  async function handleClaimRoyalty() {
    if (!contracts) return;
    setLoadingPkg(-2);
    try {
      const tx = await contracts.plan2.claimRoyalty();
      await tx.wait();
      toast.success("Royalty claimed!");
      await loadData();
    } catch (err) { toast.error(err.reason || err.message || "Royalty claim failed"); }
    finally { setLoadingPkg(null); }
  }

  if (!account) return <Navigate to="/" replace />;
  if (pageLoading) return <Loader message="Loading Global Plan..." />;
  if (userId === 0) return <Navigate to="/register" replace />;

  return (
    <div className="plan-tab">
      <SubTabs tabs={SUB_TABS} activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="plan-tab-content">
        {activeTab === "packages" && (
          <GlobalPackages
            prices={prices}
            packages={packages}
            pending={pending}
            loadingPkg={loadingPkg}
            pkgCount={PLAN2_PKG_COUNT}
            labels={PLAN2_PKG_LABELS}
            onActivate={handleActivate}
          />
        )}
        {activeTab === "income" && (
          <GlobalIncome
            incomeStats={incomeStats}
            pendingRoyalty={pendingRoyalty}
            loadingPkg={loadingPkg}
            onClaimAllGlobal={handleClaimAllGlobal}
            onClaimRoyalty={handleClaimRoyalty}
            onRefresh={loadData}
          />
        )}
        {activeTab === "team" && (
          <GlobalTeam userId={userId} contracts={contracts} maxLevels={PLAN2_MATRIX_LEVELS} />
        )}
      </div>
    </div>
  );
}
